import DataImg from './data.webp'
import FlowchartImg from './flowchart.webp'
import PortImg from './port.webp'

const APP_TITLE = 'Next Generation Trade Platform'

/**
 * Centralized content configuration for the landing page
 */

export const landingContent = {
  brand: {
    name: `CrimsonLogic | ${APP_TITLE}`,
  },
  header: {
    navLinks: [
      {
        label: "Features",
        href: "#features",
      },
      {
        label: "Benefits",
        href: "#benefits",
      },
      {
        label: "About Us",
        href: "https://crimsonlogic-northamerica.com/about-us/",
      },
      {
        label: "Contact Us",
        href: "#contact",
      },
    ],
    buttons: [
      {
        label: "Change Password",
        variant: "secondary",
        href: "/update-password",
        className: "header-btn",
      },
      {
        label: "Login",
        variant: "primary",
        href: "/login",
        sameWindow: true,
        className: "header-btn",
      },
    ],
  },
  hero: {
    label: APP_TITLE,
    title: "Powering Trade Connectivity Across North America & Beyond",
    subtitle:
      "Simplify cross-border trade with CrimsonLogic intelligent platform seamlessly integrated with CBP and the CBSA - A single interface accelerating eManifest and security filings, ensuring regulatory compliance, and delivering end-to-end cargo visibility across the U.S.-Canada border.",
    features: [
      "Centralized dashboard for cross-border operations for cross-border compliance",
      "Automated validation against CBP and CBSA rules",
      "End-to-end cross-border cargo visibility",
      "Proactive alerts for status and compliance issues",
      "Audit-ready records and submission history",
      "Scalable for growing cross-border operations",
    ],
    stats: [
      { value: "99.98%", label: "Uptime" },
      { value: "25k+/hr", label: "Filing Throughput" },
      { value: "28%", label: "Fewer Rejections" },
    ],
    cta: {
      primary: { label: "Explore Features", href: "#features" },
      secondary: { label: "Contact Sales", href: "#contact" },
    },
    image: {
      src: PortImg,
      alt: "Container port",
    },
    scrollLabel: "Scroll to explore",
  },
  features: {
    header: {
      label: "Platform Capabilities",
      title: "Smarter Data. Faster Workflows.",
      description:
        "Purpose-built integration with CBP and CBSA to simplify complex compliance, accelerate filings, and keep cross-border operations moving.",
    },
    main: [
      {
        icon: "PlaneIcon",
        title: "Air ACI/ACE",
        description:
          "Streamline air cargo compliance with automated eManifest submissions for air freight shipments crossing U.S. and Canadian borders.",
        items: [
          "Air eManifest filing",
          "Air waybill management",
          "Cargo security screening",
        ],
      },
      {
        icon: "TruckIcon",
        title: "Highway ACI/ACE",
        description:
          "Accelerate cross-border trucking with instant eManifest creation and submission for both U.S. (ACE) and Canadian (ACI) requirements.",
        items: [
          "Automated eManifest filing",
          "PAPS/PARS management",
          "Carrier compliance tools",
        ],
      },
      {
        icon: "ShipIcon",
        title: "Ocean ACI/ACE",
        description:
          "Simplify ocean freight compliance with powerful tools to manage eManifest submissions and pre-clear vessel arrivals.",
        items: [
          "Ocean eManifest filing",
          "Container tracking",
          "Vessel pre-arrival",
        ],
      },
      {
        icon: "ShieldIcon",
        title: "Air Cargo Advance Screening (ACAS)",
        description:
          "Secure air cargo screening with advance data submission to meet TSA requirements and ensure safe air freight operations.",
        items: [
          "Advance data submission",
          "TSA compliance",
          "Security screening",
        ],
      },
      {
        icon: "BellIcon",
        title: "FDA Prior Notice",
        description:
          "Enable seamless submission of Prior Notices for regulated shipments, ensuring compliance before cargo arrives at U.S. ports.",
        items: [
          "FDA Prior Notice submission",
          "Product code validation",
          "Compliance alerts",
        ],
      },
      {
        icon: "ISFIcon",
        title: "Importer Security Filing (ISF)",
        description:
          "Submit 10+2 Importer Security Filing data to CBP before ocean cargo loading to ensure compliance and avoid penalties.",
        items: [
          "10+2 ISF submission",
          "Importer/consignee data",
          "Supplier information",
        ],
      },
      {
        icon: "ServerIcon",
        title: "RNS WACM",
        description:
          "Streamline warehouse operations by automatically transmitting electronic notifications to CBSA when goods arrive.",
        items: ["Auto notifications", "CBSA integration", "Release tracking"],
      },
      {
        icon: "DocumentIcon",
        title: "ACI eHBL",
        description:
          "Automate creation and transmission of accurate electronic House Bills of Lading, reducing manual errors and accelerating clearance.",
        items: [
          "eHBL generation",
          "Auto data validation",
          "Digital signatures",
        ],
      },
    ],
    detailed: [
      {
        label: "Data Integration",
        title: "Connect Once, Use Everywhere",
        description:
          "Our platform integrates directly with your existing dispatch systems, ERP software, and customer portals. Eliminate data re-entry and ensure consistency across all trade documentation.",
        items: [
          "RESTful API for seamless integration",
          "Support for EDIFACT, X12 and XML formats",
          "Real-time data synchronization",
          "Custom integration support",
        ],
        image: DataImg,
      },
      {
        label: "Smart Automation",
        title: "Intelligent Workflow Automation",
        description:
          "Reduce manual tasks with intelligent automation that learns your workflow patterns and streamlines repetitive filing processes.",
        items: [
          "Template-based data entry",
          "Automated document generation",
          "Smart field validation",
          "Bulk submission capabilities",
        ],
        image: FlowchartImg,
      },
    ],
  },
  benefits: {
    header: {
      label: "Why Choose Us",
      title: "Our Benefits",
      description:
        "Experience the advantages of a modern, cloud-based trade declaration platform designed for today's cross-border logistics needs.",
    },
    main: [
      {
        icon: "CheckCircleIcon",
        title: "Complete Compliance",
        description:
          "Stay compliant with all mandatory Customs filings, ACI, ACE, eHBL, RNS WACM, and FDA Prior Notice through one intuitive platform.",
      },
      {
        icon: "DatabaseIcon",
        title: "Data Integration",
        description:
          "Interface with existing in-house dispatch systems, customer portals, and regulatory bodies for seamless data flow.",
      },
      {
        icon: "ClockIcon",
        title: "Time Savings",
        description:
          "Access information faster, eliminate data-entry duplication and receive real-time responses to submissions.",
      },
      {
        icon: "CurrencyDollarIcon",
        title: "Low Start-Up Costs",
        description:
          "Requires only a PC with an Internet connection. No installation or purchase of software or IT maintenance necessary.",
      },
    ],
    howItWorks: {
      title: "How It Works",
      description:
        "Get started in minutes with our streamlined onboarding process",
      steps: [
        {
          number: "01",
          title: "Connect",
          description:
            "Integrate with your existing systems or use our web interface directly.",
        },
        {
          number: "02",
          title: "Create",
          description:
            "Generate eManifests and trade documents with our smart templates.",
        },
        {
          number: "03",
          title: "Submit",
          description: "Submit directly to CBP and CBSA with one click.",
        },
        {
          number: "04",
          title: "Track",
          description:
            "Monitor status in real-time and receive instant notifications.",
        },
      ],
    },
    cta: {
      title: "Ready to Streamline Your Trade Operations?",
      description:
        "Join hundreds of logistics companies already using our platform to simplify their cross-border trade declarations.",
      label: "Get Started Today",
      href: "https://crimsonlogic-northamerica.com/book-a-consultation/",
    },
  },
  contact: {
    header: {
      label: "Get in Touch",
      title: "Contact Us",
      description:
        "We're here to help. Reach out to our support or sales teams for assistance with your trade compliance needs.",
    },
    systemSupport: {
      title: "System Support",
      description:
        "For system related query, feedback or problem reporting, please contact the below Call Centre:",
      company: "CrimsonLogic Customer Support",
      tollFree: "+1 877 763 6887",
      tel: "+1 905 763 6887",
      fax: "+1 905 763 2321",
      email: "support@crimsonlogic.com",
    },
    salesSupport: {
      title: "Sales and Marketing Support",
      locations: [
        {
          id: "canada",
          country: "Canada",
          code: "CA",
          company: "CrimsonLogic (Canada) Inc.",
          address: [
            "100 York Boulevard, Suite 260,",
            "Richmond Hill, Ontario, Canada L4B 1J8",
          ],
          telephone: "+1 905 763 6887",
          fax: "+1 905 763 2321",
          email: "salesna@crimsonlogic.com",
          website: "https://crimsonlogic-northamerica.com/",
        },
        {
          id: "usa",
          country: "USA",
          code: "US",
          company: "CrimsonLogic (U.S.), Inc.",
          address: ["19800 MacArthur Blvd.", "Suite 300 Irvine,", "CA 92612"],
          telephone: "+1 905 763 6887",
          fax: "+1 905 763 2321",
          email: "salesna@crimsonlogic.com",
          website: "https://crimsonlogic-northamerica.com/",
        },
        {
          id: "mexico",
          country: "Mexico",
          code: "MX",
          company: "CrimsonLogic Mexico, S.A. DE C.V.",
          address: [
            "IZA BC Torre Platino,",
            "Blvd. Sanchez Taboada 10488,",
            "Zona Urbana Río Tijuana,",
            "Tijuana, Baja California",
            "22010",
          ],
          telephone: "+52 664 615 8000",
          email: "salesna@crimsonlogic.com",
          website: "https://crimsonlogic-northamerica.com/",
        },
      ],
    },
  },
  footer: {
    name: APP_TITLE,
    description:
      "Simplifying cross-border trade between the U.S., Mexico and Canada with next-generation digital compliance solutions.",
    social: [
      {
        label: "LinkedIn",
        href: "https://www.linkedin.com/company/crimsonlogic",
        icon: "LinkedInIcon",
      },
    ],
    features: {
      label: "Products",
    },
    company: [
      {
        label: "About Us",
        href: "https://crimsonlogic-northamerica.com/about-us/",
      },
      { label: "Contact Us", href: "#contact" },
    ],
    copyright: {
      label: "© {year} CrimsonLogic Pte Ltd",
      href: "https://www.crimsonlogic.com/",
    },
    legal: [
      {
        label: "Privacy Policy",
        href: "https://crimsonlogic-northamerica.com/privacy-policy/",
      },
      {
        label: "Terms of Use",
        href: "https://crimsonlogic-northamerica.com/terms-of-use/",
      },
      {
        label: "Terms of Service",
        href: "https://www.crimsonlogic.com/saas-general-terms-and-conditions",
      },
    ],
  },
}
